import React, {useEffect, useRef, useState} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import API from '../../../../../app/api';
import _ from 'lodash';
import {mapObservationFromAPI} from '../obsUtils';
import {Button, FormControlLabel, Switch} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import ExpandIcon from '@mui/icons-material/Expand';
import {keyContent, rowContent, sideMap} from './obsChartData';
import Slider from '@mui/material/Slider';
import $ from 'jquery';
import {useDispatch, useSelector} from 'react-redux';
import {agylePref, saveAgylePreference} from '../../../../../store/reducers/preferences';
import AgylaCanvas from '../../../../../components/map/AgylaCanvas';
import config from '../../../../../app/config';

const useStyles = makeStyles((theme) => ({
    modal: {
        position: 'fixed',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        padding: 10,
    },
    chartHeaders: {
        height: 'calc(100% - 120px)',
        float: 'left',
        backgroundColor: 'white',
        overflow: 'hidden',
    },
    chartBody: {
        height: 'calc(100% - 220px)',
        backgroundColor: 'white',
        overflow: 'hidden',
    },
    closeButton: {
        float: 'right',
    },
    additionalButton: {
        marginLeft: 20,
        marginRight: 20,
        float: 'left',
    },
    noObsText: {
        textAlign: 'center',
        width: '100%',
        fontSize: 20,
        position: 'absolute',
        color: 'black',
        top: 100,
    },
    sliderZoom: {
        width: 200,
        float: 'left',
        paddingTop: 10,
    },
}));

const ObservationChart = (props) => {
    const classes = useStyles();
    const controller = new AbortController();
    const dispatch = useDispatch();

    // Selectors
    const agylePreference = useSelector(agylePref);

    // cell sizes
    const cellHeight = 20;
    const cellWidth = 60;
    const firstCellWidth = 200;

    // tab size
    const rowCount = 63;
    const [columnCount, setColumnCount] = React.useState(0);

    // charts place in tab
    const chartStartCell = 4;
    const chartBPHeightInCell = 26;
    const chartTempHeightInCell = 15;

    // blood pressure chart range
    const chartHighBP = 260;
    const chartLowBP = 0;

    // temperature chart range
    const chartHighTemp = 46;
    const chartLowTemp = 31;

    const unitTemp = (chartHighTemp - chartLowTemp) / chartTempHeightInCell;
    const unitBP = (chartHighBP - chartLowBP) / chartBPHeightInCell;
    const fontSize = 12;

    const canvasRef = useRef(null);

    const borderColor = '#b3a6a6';

    const tableHeight = rowCount * cellHeight;
    const modalHeight = document.getElementById('canvas-containe')?.clientHeight || 680;
    const fitToHeightZoom = modalHeight / tableHeight;
    const initialZoom = 1;

    const MAX_ZOOM = 1.5;
    const MIN_ZOOM = 0.5;
    const SCROLL_SENSITIVITY = 0.0001;

    const leftSpace = 200;
    const scrollWidth = 20;

    const [obs, setObservations] = React.useState([]);

    const [showDeletedObs, setShowDeleted] = React.useState(false);

    const zoomRef = useRef(initialZoom);
    const [wheelZoom, setWheelZoom] = React.useState(agylePreference.chartObs.wheel === 'zoom');

    const obsToDraw = React.useMemo(
        () => {
            if (obs && obs.length > 0) {
                let obsToDraw = [];
                _.forEach(obs, (apiObs) => {
                    if (showDeletedObs || apiObs.status !== 'cancelled') {
                        obsToDraw.push(mapObservationFromAPI(apiObs, true));
                    }
                });
                setColumnCount(Math.max(obsToDraw.length, 7));
                return obsToDraw;
            } else {
                return [];
            }
        },
        [obs, showDeletedObs],
    );

    const getTransformedPoint = (x, y) => {
        const ctx = canvasRef?.current?.getContext('2d');
        if (ctx) {
            const originalPoint = new DOMPoint(x, y);
            return ctx.getTransform().invertSelf().transformPoint(originalPoint);
        }
    };

    const setBoundaries = () => {
        const canvasContainer = $('#obs')[0];
        const context = canvasRef.current.getContext('2d');

        const currentTransformedLT = getTransformedPoint(0, 0);
        const currentTransformedLB = getTransformedPoint(canvasContainer.clientWidth - firstCellWidth * zoomRef.current, canvasContainer.clientHeight - scrollWidth);
        const currentTransformedRT = getTransformedPoint(canvasContainer.clientWidth, 0);

        if (currentTransformedLT.y < 0) {
            context.translate(0, currentTransformedLT.y);
        }

        if (currentTransformedLB.y > tableHeight) {
            context.translate(0, currentTransformedLB.y - tableHeight);
        }

        let canvasWidth = (cellWidth * columnCount + leftSpace + firstCellWidth);
        let scrollHorWidth = canvasContainer.clientWidth - (firstCellWidth * zoomRef.current);
        let canvasBodyWidth = columnCount * cellWidth + leftSpace;

        if (currentTransformedLT.x < 0) {
            context.translate(currentTransformedLT.x, 0);
        }

        if (currentTransformedRT.x > canvasWidth && scrollHorWidth > scrollHorWidth * scrollHorWidth / (canvasBodyWidth * zoomRef.current)) {
            context.translate(currentTransformedRT.x - canvasWidth, 0);
        }
    };

    const drawChart = () => {
        setBoundaries();
        drawBody();
        drawHeaders();
    };

    useEffect(() => {
        if (obsToDraw.length) {
            reDraw();
        }
    }, [canvasRef, obsToDraw]);

    const drawBody = () => {
        if (canvasRef.current.getContext) {
            drawEmptyBody();
            drawChartData();
            drawKeyTexts();
            drawScroll();
        }
    };

    const bar1 = useRef(null);
    const bar2 = useRef(null);

    const drawRoundedRect = (ctx, x, y, width, height, radius) => {
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
    };

    const drawScroll = () => {
        const ctx = canvasRef.current.getContext('2d');
        const canvasContainer = $('#obs')[0];
        const currentTransformedLT = getTransformedPoint(0, 0);

        let x = currentTransformedLT.x > 0 ? currentTransformedLT.x : 0;
        let canvasHeight = cellHeight * rowCount;
        let canvasWidth = columnCount * cellWidth + leftSpace;
        let scrollVerHeight = canvasContainer.clientHeight - scrollWidth;
        let scrollHorWidth = canvasContainer.clientWidth - (firstCellWidth * zoomRef.current);

        const thumbColor = '#9E9E9E';
        const trackColor = 'transparent';
        const thumbRadius = 6;
        const scrollbarWidth = 10;

        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);

        // === Vertical scroll ===
        if (scrollVerHeight > (scrollVerHeight * scrollVerHeight / (canvasHeight * zoomRef.current))) {
            // Track
            ctx.fillStyle = trackColor;
            ctx.fillRect(
                canvasRef.current.width - (2 * scrollbarWidth),
                0,
                scrollbarWidth,
                scrollVerHeight
            );

            // Thumb
            const thumbY = (currentTransformedLT.y * scrollVerHeight / canvasHeight);
            const thumbHeight = (scrollVerHeight * scrollVerHeight / (canvasHeight * zoomRef.current));

            ctx.fillStyle = thumbColor;
            drawRoundedRect(ctx,
                canvasRef.current.width - 1.5*scrollbarWidth,
                thumbY,
                scrollbarWidth,
                thumbHeight,
                thumbRadius
            );
            ctx.fill();

            bar1.current = new Path2D();
            bar1.current.rect(canvasRef.current.width - (2*scrollbarWidth), thumbY, scrollbarWidth, thumbHeight);
        }

        // === Horizontal scroll ===
        if (scrollHorWidth > (scrollHorWidth * scrollHorWidth / (canvasWidth * zoomRef.current))) {
            // Track
            ctx.fillStyle = trackColor;
            ctx.fillRect(
                firstCellWidth * zoomRef.current,
                canvasContainer.clientHeight - scrollbarWidth,
                scrollHorWidth,
                scrollbarWidth
            );

            // Thumb
            const thumbX = firstCellWidth * zoomRef.current + (x * scrollHorWidth) / (canvasWidth);
            const thumbWidth = (scrollHorWidth * scrollHorWidth / (canvasWidth * zoomRef.current));

            ctx.fillStyle = thumbColor;
            drawRoundedRect(ctx,
                thumbX,
                canvasContainer.clientHeight - scrollbarWidth,
                thumbWidth,
                scrollbarWidth,
                thumbRadius
            );
            ctx.fill();

            bar2.current = new Path2D();
            bar2.current.rect(thumbX, canvasContainer.clientHeight - scrollbarWidth, thumbWidth, scrollbarWidth);
        }

        ctx.restore();
    };

    const isMouseClicked = useRef(false);
    const isMouseClicked2 = useRef(false);
    const previousMousePositionY = useRef(0);
    const previousMousePositionX = useRef(0);

    const handlePointerDown = (event) => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);

        if (ctx.isPointInPath(bar1.current, event.offsetX, event.offsetY)) {
            isMouseClicked.current = true;
        }

        if (ctx.isPointInPath(bar2.current, event.offsetX, event.offsetY)) {
            isMouseClicked2.current = true;
        }

        ctx.restore();
    };

    const handlePointerMove = (event) => {
        const ctx = canvasRef.current.getContext('2d');
        if (isMouseClicked.current) {
            if (previousMousePositionY.current) {
                let mouseMovement = event.clientY - previousMousePositionY.current;
                ctx.translate(0, -mouseMovement * 2);
                reDraw();
            }
            previousMousePositionY.current = event.clientY;
            event.stopPropagation();
            event.preventDefault();
        }
        if (isMouseClicked2.current) {
            if (previousMousePositionX.current) {
                let mouseMovement = event.clientX - previousMousePositionX.current;
                ctx.translate(-mouseMovement, 0);
                reDraw();
            }
            previousMousePositionX.current = event.clientX;
            event.stopPropagation();
            event.preventDefault();
        }
    };

    const handlePointerUp = (event) => {
        isMouseClicked.current = false;
        isMouseClicked2.current = false;
        previousMousePositionX.current = null;
        previousMousePositionY.current = null;
    };

    useEffect(() => {
        if (canvasRef.current) {
            canvasRef.current.addEventListener('pointerdown', handlePointerDown);
            canvasRef.current.addEventListener('pointermove', handlePointerMove);
            canvasRef.current.addEventListener('pointerup', handlePointerUp);
            canvasRef.current.addEventListener('mouseleave', handlePointerUp);

        }
        return () => {
            if (canvasRef.current) {
                canvasRef.current.removeEventListener('pointerdown', handlePointerDown);
                canvasRef.current.removeEventListener('pointermove', handlePointerMove);
                canvasRef.current.removeEventListener('pointerup', handlePointerUp);
                canvasRef.current.removeEventListener('mouseleave', handlePointerUp);
            }
        };
    }, [canvasRef.current, obsToDraw.length]);

    useEffect(() => {
        getObservation();
    }, []);

    const getObservation = () => {
        API.getFullObservations(props.patient, props.type, false, controller.signal)
            .then((result) => {
                setObservations(result);
            })
            .catch((error) => {
                console.error('Observations.API.getObservations: ', error);
            });
    };

    const drawEmptyBody = () => {
        const ctx = canvasRef.current.getContext('2d');
        for (let j = 0; j < rowCount; j++) {
            for (let i = 0; i < columnCount; i++) {
                ctx.strokeStyle = borderColor;
                ctx.strokeRect(i * cellWidth + firstCellWidth, j * cellHeight, cellWidth, cellHeight);
            }
            ctx.strokeStyle = '#655d5d';
            if (j === chartStartCell) ctx.strokeRect(firstCellWidth, j * cellHeight, cellWidth * (columnCount), 1);
            if (j === chartStartCell + chartBPHeightInCell) ctx.strokeRect(firstCellWidth, j * cellHeight, cellWidth * (columnCount), 1);
            if (j < chartStartCell + chartTempHeightInCell && j % 2 === 1 && j > chartStartCell) {
                ctx.fillStyle = '#000000';
                ctx.textAlign = 'left';
                ctx.fillText(chartHighTemp - (j - chartStartCell) * unitTemp + '°C', (columnCount) * cellWidth + 5 + firstCellWidth, j * cellHeight + (fontSize / 2) - 1);
            }
            if (rowContent[j] && (rowContent[j].verticalTitle || rowContent[j].verticalTitle2)) {
                ctx.strokeRect(firstCellWidth, j * cellHeight, cellWidth * (columnCount), 1);
            }
        }
    };
    const drawKeyTexts = () => {
        const ctx = canvasRef.current.getContext('2d');

        ctx.font = 'bold 14px Verdana';
        ctx.textAlign = 'left';
        ctx.fillStyle = '#000000';
        ctx.fillText('KEY', cellWidth * (columnCount) + 10 + firstCellWidth, 25 * cellHeight);

        let margin = 0;

        keyContent.forEach((key, index) => {
            ctx.font = key.font;
            if (key.isTitle) margin += 15;
            ctx.fillText(key.text, cellWidth * (columnCount) + 10 + firstCellWidth, (26 + index) * cellHeight + margin);
        });
    };
    const drawChartData = () => {
        for (let i = 0; i < obsToDraw.length; i++) {
            let xScore = cellWidth * i + firstCellWidth;

            _.forEach(Object.keys(rowContent), (rowNumber) => {
                if (rowContent[rowNumber].dataType) {
                    switch (obsToDraw[i][rowContent[rowNumber].field]?.status) {
                        case 'completed':
                        case 'new':
                        case 'partial':
                            drawValueToCell(rowNumber, xScore, i);
                            break;
                        case 'cancelled':
                            drawValueToCell(rowNumber, xScore, i);
                            break;
                        case 'notRecorded':
                            drawDataToCell('NR', xScore, rowNumber * 1 + 1);
                            break;
                        case 'equipment':
                            drawDataToCell('EQ', xScore, rowNumber * 1 + 1);
                            break;
                        case 'patientRefused':
                            drawDataToCell('R', xScore, rowNumber * 1 + 1);
                            break;
                        case 'patientCondition':
                            drawDataToCell('PC', xScore, rowNumber * 1 + 1);
                            break;
                        default:
                            drawDataToCell('', xScore, rowNumber * 1 + 1);
                            break;
                    }
                }
            });

            if (obsToDraw[i].bloodPressure?.status === 'completed') {
                drawBloodPressure(canvasRef, obsToDraw[i].bloodPressure.systolicPressure, obsToDraw[i].bloodPressure.diastolicPressure, xScore);
            }
            if (obsToDraw[i].pulseRate?.status === 'completed') {
                drawPulse(canvasRef, obsToDraw[i].pulseRate.pulseRate, obsToDraw[i].pulseRate.pulseType === 'irregular', xScore);

            }
            if (obsToDraw[i].temperature?.status === 'completed') {
                drawTemp(canvasRef, obsToDraw[i].temperature.temperature, xScore);
            }

            let y = obsToDraw[i].investigation === 'neuro' ? 47 : 43;
            drawScoreBackground(obsToDraw[i].totalScore, xScore, y, obsToDraw[i].investigation === 'neuro', true);
            drawDataToCell(obsToDraw[i].totalScore, xScore, y);

            drawDates(obsToDraw[i].startedDate, xScore);
            drawNextObs(i, xScore);

            if (i === 0 || (i > 0 && i < obsToDraw.length - 1 && obsToDraw[i].startedDate.format('MM') !== obsToDraw[i - 1].startedDate.format('MM'))) {
                drawDataToCell(obsToDraw[i].startedDate.format('MMM'), xScore, 1);
            }

            if (obsToDraw[i].status === 'cancelled')
                drawCancelledBackground(xScore);
        }
        drawTempLine();
        drawPulseLine();
    };

    const tempColor = config.chartTemperatureColor || '#8702c7';

    const drawTempLine = () => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.beginPath();
        ctx.strokeStyle = tempColor;
        ctx.fillStyle = tempColor;
        let x1, y1, x2, y2;
        for (let i = 0; i < obsToDraw.length - 1; i++) {
            if (obsToDraw[i].temperature?.status === 'completed' && obsToDraw[i + 1].temperature?.status === 'completed') {
                x1 = cellWidth * i + cellWidth / 2 + firstCellWidth;
                y1 = getTempY(obsToDraw[i].temperature.temperature);
                x2 = cellWidth * (i + 1) + cellWidth / 2 + firstCellWidth;
                y2 = getTempY(obsToDraw[i + 1].temperature.temperature);

                ctx.moveTo(x1, y1);
                ctx.lineTo(x1, y1);
                ctx.lineTo(x2, y2);
                ctx.fill();
                ctx.stroke();
            }
        }
    };

    const drawPulseLine = () => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.beginPath();
        ctx.strokeStyle = '#0a3af6';
        ctx.fillStyle = '#0924f6';
        let x1, y1, x2, y2;
        for (let i = 0; i < obsToDraw.length - 1; i++) {
            if (obsToDraw[i].pulseRate?.status === 'completed' && obsToDraw[i + 1].pulseRate?.status === 'completed') {
                x1 = cellWidth * i + cellWidth / 2 + firstCellWidth;
                y1 = getPulseY(obsToDraw[i].pulseRate.pulseRate);
                x2 = cellWidth * (i + 1) + cellWidth / 2 + firstCellWidth;
                y2 = getPulseY(obsToDraw[i + 1].pulseRate.pulseRate);

                ctx.moveTo(x1, y1);
                ctx.lineTo(x1, y1);
                ctx.lineTo(x2, y2);
                ctx.fill();
                ctx.stroke();
            }
        }

    };
    const drawCancelledBackground = (x) => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.fillStyle = 'rgba(99,99,99,0.56)';
        ctx.fillRect(x, 0, cellWidth, cellHeight * rowCount);
        ctx.fillStyle = '#ffffff';
    };

    const drawNextObs = (i, xScore) => {
        if (obsToDraw[i].nextObsDate) {
            let valueObs = obsToDraw[i].nextObsDate.diff(obsToDraw[i].completedDate, 'minute');
            if (valueObs > 0) {
                if (valueObs > 60) {
                    let valueH = Math.floor(valueObs / 60);
                    drawDataToCell(valueH + 'h' + (valueObs - valueH * 60) + 'm', xScore, 4);
                } else drawDataToCell(valueObs + 'm', xScore, 4);
            }
        }
    };

    const drawDates = (date, x) => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.beginPath();
        ctx.textAlign = 'center';
        ctx.font = '15px Verdana';
        ctx.fillStyle = '#000000';
        ctx.fillText(date.format('DD'), x + cellWidth / 2, 2 * cellHeight - 5);
        ctx.fillText(date.format('HH:mm'), x + cellWidth / 2, 3 * cellHeight - 5);

    };
    const getTempY = (temp) => {
        return chartStartCell * cellHeight + ((chartHighTemp - temp) * chartTempHeightInCell * cellHeight / (chartHighTemp - chartLowTemp));
    };
    const getPulseY = (pulse) => {
        return chartStartCell * cellHeight + ((chartHighBP - pulse) * chartBPHeightInCell * cellHeight / (chartHighBP - chartLowBP));
    };
    const drawTemp = (canvas, temp, x, key = false) => {
        x = x + cellWidth / 2;
        const ctx = canvas.current.getContext('2d');

        let y = getTempY(temp);
        ctx.strokeStyle = tempColor;
        ctx.fillStyle = tempColor;
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();

        if (key) {
            ctx.textAlign = 'left';
            ctx.fillText('TEMPERATURE = ', x - 110, y + 2);
        }
        ctx.fillStyle = '#000000';
    };
    const drawPulse = (canvas, pulse, irregular, x, key = false) => {
        x = x + cellWidth / 2;

        const ctx = canvas.current.getContext('2d');
        ctx.strokeStyle = '#0a3af6';
        ctx.fillStyle = '#0924f6';

        let y = getPulseY(pulse);

        if (irregular) {
            ctx.fillText('i', x - 10, y - 5);
        }

        ctx.beginPath();

        ctx.moveTo(x, y);
        ctx.lineTo(x + 5, y + 5);
        ctx.lineTo(x - 5, y - 5);
        ctx.fill();
        ctx.stroke();

        ctx.beginPath();

        ctx.moveTo(x, y);
        ctx.lineTo(x - 5, y + 5);
        ctx.lineTo(x + 5, y - 5);
        ctx.fill();
        ctx.stroke();

        if (key) {
            ctx.textAlign = 'left';
            ctx.fillText('PULSE = ', x - 110, y);
        }
        ctx.fillStyle = '#000000';
    };
    const drawBloodPressure = (canvas, bp1, bp2, x, key = false) => {
        x = x + cellWidth / 2;

        const ctx = canvas.current.getContext('2d');
        ctx.strokeStyle = '#000000';
        let size = 8;
        let sizeX = 3;

        let y = chartStartCell * cellHeight + ((chartHighBP - bp1) * chartBPHeightInCell * cellHeight / (chartHighBP - chartLowBP));
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + sizeX, y + size);
        ctx.lineTo(x - sizeX, y + size);
        ctx.fill();
        ctx.stroke();
        ctx.fill();

        let y2 = chartStartCell * cellHeight + ((chartHighBP - bp2) * chartBPHeightInCell * cellHeight / (chartHighBP - chartLowBP));
        ctx.beginPath();
        ctx.moveTo(x, y2);
        ctx.lineTo(x + sizeX, y2 - size);
        ctx.lineTo(x - sizeX, y2 - size);
        ctx.fill();
        ctx.stroke();
        ctx.fill();

        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x, y2);
        ctx.fill();
        ctx.stroke();

        if (key) {
            ctx.textAlign = 'left';
            ctx.fillText('BLOOD', x - 110, y + (y2 - y) / 2);
            ctx.fillText('PRESSURE =', x - 110, 15 + y + (y2 - y) / 2);
        }
    };
    const drawLimb = (limbMovement, x, y, rowValue, type = {first: 'arm', second: 'secondArm'}) => {
        if (limbMovement.movement?.description === rowValue && limbMovement.secondMovement?.description === rowValue) {
            drawDataToCell('R - L', x, y);
        } else {
            if (limbMovement.movement?.description === rowValue)
                drawDataToCell(sideMap[limbMovement[type.first]], x, y);
            if (limbMovement.secondMovement?.description === rowValue)
                drawDataToCell(sideMap[limbMovement[type.second]], x, y);
        }

    };
    const drawDataToCell = (rate, x, y, small = false) => {
        if (rate === null) rate = '-';
        if (rate || rate === 0) {
            y = y * cellHeight - 5;
            const ctx = canvasRef.current.getContext('2d');
            ctx.beginPath();
            ctx.textAlign = 'center';
            ctx.font = '12px Verdana';

            if (small) {
                ctx.font = '8px Verdana';
                ctx.textAlign = 'right';
                x = x + cellWidth;
                y = y - cellHeight / 3;
            } else {
                x = x + cellWidth / 2;
            }

            ctx.fillText(rate, x, y);
            ctx.fillStyle = '#000000';

        }
    };
    const drawValueToCell = (rowNumber, xScore, i) => {
        const y = rowNumber * 1 + 1;

        switch (rowContent[rowNumber].dataType) {
            case 'score':
                if (obsToDraw[i][rowContent[rowNumber].field].score !== null) {
                    drawScoreBackground(obsToDraw[i][rowContent[rowNumber].field].score, xScore, y, rowContent[rowNumber].isNeuro);
                    drawDataToCell(obsToDraw[i][rowContent[rowNumber].field].score, xScore, y);
                } else {
                    let v = rowContent[rowNumber].defaultField ? _.get(obsToDraw[i], rowContent[rowNumber].field + '.' + rowContent[rowNumber].defaultField) : '-';
                    drawDataToCell(v, xScore, y);
                }


                break;
            case 'fieldValue':
                if (rowContent[rowNumber].valueToCheck && !_.get(obsToDraw[i], rowContent[rowNumber].field + '.' + rowContent[rowNumber].valueToCheck)) {
                    drawDataToCell('C', xScore, y);
                } else {
                    drawScoreBackground(obsToDraw[i][rowContent[rowNumber].field].score, xScore, y, rowContent[rowNumber].isNeuro);
                    drawDataToCell(obsToDraw[i][rowContent[rowNumber].field][rowContent[rowNumber].value], xScore, y);
                }

                break;
            case 'fieldValueMap':
                drawScoreBackground(obsToDraw[i][rowContent[rowNumber].field].score, xScore, y, rowContent[rowNumber].isNeuro);
                drawDataToCell(rowContent[rowNumber].mapper[obsToDraw[i][rowContent[rowNumber].field][rowContent[rowNumber].value]?.id], xScore, y);
                break;
            case 'limbValue':
                drawLimb(obsToDraw[i][rowContent[rowNumber].field], xScore, y, rowContent[rowNumber].rowValue, {
                    first: 'leg',
                    second: 'secondLeg',
                });
                break;
            case 'limbValueArms':
                drawLimb(obsToDraw[i][rowContent[rowNumber].field], xScore, y, rowContent[rowNumber].rowValue, {
                    first: 'arm',
                    second: 'secondArm',
                });
                break;
            case 'fieldValueBoolean':
                if (rowContent[rowNumber].valueToCheck && !_.get(obsToDraw[i], rowContent[rowNumber].field + '.' + rowContent[rowNumber].valueToCheck)) {
                    drawDataToCell('C', xScore, y);
                } else {
                    let value = obsToDraw[i][rowContent[rowNumber].field][rowContent[rowNumber].value] ? 'YES' : 'NO';
                    drawDataToCell(value, xScore, y);
                }
                break;
            case 'fieldValueWithUnit':
                drawScoreBackground(obsToDraw[i][rowContent[rowNumber].field].score, xScore, y, rowContent[rowNumber].isNeuro);
                let unit = obsToDraw[i][rowContent[rowNumber].field][rowContent[rowNumber].unit] ? '(S2)' : '';
                drawDataToCell(obsToDraw[i][rowContent[rowNumber].field][rowContent[rowNumber].value], xScore, y);
                drawDataToCell(unit, xScore, y, true);
                break;
        }
    };
    const drawScoreBackground = (score, x, y, isNeuro = false, isTotalScore) => {
        const ctx = canvasRef.current.getContext('2d');

        ctx.fillStyle = '#ffffff';

        let border = 1;

        if (isNeuro) {
            if (isTotalScore) {
                ctx.fillStyle = '#000000';
            } else {
                if (score >= 1) {
                    ctx.fillStyle = '#ef5350';
                }
                if (score === 3) {
                    ctx.fillStyle = '#ff9800';
                }
                if (score >= 4) {
                    ctx.fillStyle = '#4caf50';
                }

                ctx.fillRect(x + border, (y - 1) * cellHeight + border, cellWidth - border * 2, cellHeight - border * 2);
                ctx.fillStyle = '#ffffff';
            }

        } else {
            if (isTotalScore) {
                ctx.fillStyle = '#4caf50';
                if (score >= 5 && score <= 6) {
                    ctx.fillStyle = '#ff9800';
                }
                if (score >= 7) {
                    ctx.fillStyle = '#ef5350';
                }

                ctx.fillRect(x + border, (y - 1) * cellHeight + border, cellWidth - border * 2, cellHeight - border * 2);
                ctx.fillStyle = '#ffffff';
            } else {

                if (score > 1) {
                    ctx.fillStyle = '#4caf50';
                }
                if (score === 2) {
                    ctx.fillStyle = '#ff9800';
                }
                if (score >= 3) {
                    ctx.fillStyle = '#ef5350';
                }
                if (score > 1) {
                    ctx.fillRect(x + border, (y - 1) * cellHeight + border, cellWidth - border * 2, cellHeight - border * 2);
                    ctx.fillStyle = '#ffffff';
                } else {
                    ctx.fillStyle = '#000000';
                }
            }
        }
    };

    const drawHeaders = () => {
        if (canvasRef.current.getContext) {
            const ctx = canvasRef.current.getContext('2d');
            const currentTransformedLT = getTransformedPoint(0, 0);

            ctx.font = '10px Verdana';
            ctx.fillStyle = '#d9d9d9';

            ctx.fillRect(currentTransformedLT.x, 0, firstCellWidth, cellHeight * rowCount);
            ctx.fillStyle = '#000000';


            for (let j = 0; j < rowCount; j++) {
                ctx.textAlign = 'left';
                ctx.strokeStyle = '#655d5d';
                if (rowContent[j] && rowContent[j].type === 'string') {
                    ctx.fillText(rowContent[j].title, currentTransformedLT.x + 5 + rowContent[j].size * firstCellWidth, (j + 1) * cellHeight - 5);
                    ctx.strokeRect(currentTransformedLT.x + rowContent[j].size * firstCellWidth, j * cellHeight, firstCellWidth - firstCellWidth * rowContent[j].size, cellHeight);

                    if (rowContent[j].verticalTitle) {
                        additionalTitle(j, rowContent[j].verticalTitle.endCell, rowContent[j].size, rowContent[j].verticalTitle.titles);
                    }

                    if (rowContent[j].verticalTitle2) {
                        additionalTitle(j, rowContent[j].verticalTitle2.endCell, rowContent[j].size, rowContent[j].verticalTitle2.titles, firstCellWidth / 3);
                    }
                }

                if (j === chartStartCell) ctx.strokeRect(currentTransformedLT.x, j * cellHeight, firstCellWidth, 1);
                if (j === chartStartCell + chartBPHeightInCell) ctx.strokeRect(currentTransformedLT.x, j * cellHeight, firstCellWidth, 1);

                ctx.strokeStyle = borderColor;
                if (j % 2 === 1 && j > chartStartCell) {
                    if (j < chartStartCell + chartBPHeightInCell) {
                        ctx.fillStyle = '#000000';
                        ctx.textAlign = 'right';
                        ctx.fillText(chartHighBP - (j - chartStartCell) * unitBP + '', currentTransformedLT.x + firstCellWidth - 5, j * cellHeight + (fontSize / 2) - 1);
                    }
                }
            }
            let x = currentTransformedLT.x + 110;
            drawBloodPressure(canvasRef, 180, 130, x, true);
            drawPulse(canvasRef, 65, true, x, true);
            drawTemp(canvasRef, 41, x, true);

            ctx.textAlign = 'left';
        }
    };
    const additionalTitle = (startCell, endCell, size, titles, addx = 0) => {
        const ctx = canvasRef.current.getContext('2d');
        const currentTransformedLT = getTransformedPoint(0, 0);

        let w = (addx ? firstCellWidth - addx - firstCellWidth * size : firstCellWidth * size);
        ctx.strokeRect(addx + currentTransformedLT.x, startCell * cellHeight, w, cellHeight * (endCell - startCell));

        ctx.strokeStyle = '#655d5d';
        ctx.strokeRect(addx + currentTransformedLT.x, startCell * cellHeight, firstCellWidth - addx, 1);
        ctx.strokeStyle = borderColor;

        ctx.save();
        ctx.translate(0, endCell * cellHeight);
        ctx.rotate(-Math.PI / 2);
        ctx.textAlign = 'center';


        titles.forEach((title, index) => {
            ctx.fillText(title, ((endCell - startCell) * cellHeight) / 2, addx + 15 * (index + 1) + currentTransformedLT.x);
        });

        ctx.restore();
    };

    const setZoom = (zoom) => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(zoom, zoom);
        zoomRef.current = zoom;
        reDraw();
    };

    const setFullWidthZoom = () => {
        setZoom((window.innerWidth - 60) / (columnCount * cellWidth + firstCellWidth));
    };

    const setFullHeightZoom = () => {
        setZoom(fitToHeightZoom);
    };

    const handleChange = (event, newValue) => {
        setZoom(newValue);
    };

    const changeObsWheelPref = (e) => {
        setWheelZoom(e.target.checked);
        dispatch(saveAgylePreference({chartObs: {wheel: e.target.checked ? 'zoom' : 'scroll'}}));
    };

    const [size, setSize] = useState({width: 300, height: 400});

    useEffect(() => {
        if (document.getElementById('obs-cont'))
            setSize({
                width: document.getElementById('obs-cont').clientWidth,
                height: document.getElementById('obs-cont').clientHeight,
            });
    }, [window.innerWidth, document.getElementById('obs-cont')]);

    const reDraw = () => {
        if (canvasRef.current && canvasRef.current.getContext) {
            const ctx = canvasRef.current.getContext('2d');
            const {width, height} = canvasRef.current;

            ctx.save();
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.clearRect(0, 0, width, height);
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, width, height);

            ctx.restore();

            drawChart();
        }
    };

    return (
        <div className={classes.modal}>
            {!obs && obsToDraw.length === 0 && <div className={classes.noObsText}> NO OBS</div>}

            <div className={classes.chartBody} id="obs-cont">
                <AgylaCanvas
                    id="obs"
                    width={size.width}
                    height={size.height}
                    reference={canvasRef}
                    reDraw={reDraw}
                    wheelZoom={wheelZoom}
                    zoomRef={zoomRef}
                />
            </div>

            {obsToDraw.length !== 0 &&
                <div className={classes.additionalButton}>
                    <FormControlLabel
                        control={<Switch checked={showDeletedObs}
                                         onChange={e => setShowDeleted(e.target.checked)}/>}
                        label={'Show cancelled'}/>

                    <FormControlLabel
                        control={<Switch checked={wheelZoom} onChange={changeObsWheelPref}/>}
                        label={'Wheel zoom'}/>

                    <IconButton
                        id="full-height"
                        size="small"
                        onClick={setFullHeightZoom}
                    >
                        <ExpandIcon/>
                    </IconButton>
                    <IconButton
                        id="full-width"
                        size="small"
                        onClick={setFullWidthZoom}
                    >
                        <ExpandIcon style={{rotate: '90deg'}}/>
                    </IconButton>
                </div>
            }

            {!wheelZoom && obsToDraw.length !== 0 &&
                <div className={classes.sliderZoom}>
                    <Slider
                        step={SCROLL_SENSITIVITY}
                        min={MIN_ZOOM}
                        max={MAX_ZOOM}
                        defaultValue={zoomRef.current} onChange={handleChange}/>
                </div>
            }
        </div>
    );
};

export default ObservationChart;